import { Suspense, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Loader2, Menu, MessageSquareDot, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { EnvBadge } from './EnvBadge'
import { PageFeedback } from '@/components/PageFeedback'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { CommandPalette } from '@/components/CommandPalette'
import { cn } from '@/lib/cn'

// Pages where the floating feedback overlay shouldn't render — pre-auth
// login and the transient /outlook/return handoff. Everywhere else (incl.
// /ai) shows the bottom-right Feedback button.
const HIDE_FEEDBACK = ['/login', '/outlook/return']

// Focus mode: the desktop nav column can be collapsed away so long documents (the
// RFP response builder especially) get the full width. Persisted, because someone
// who wants to focus wants to STAY focused across navigations and reloads.
const NAV_COLLAPSED_KEY = 'penser.navCollapsed'

export function AppShell() {
  const { pathname } = useLocation()
  const hideFeedback = HIDE_FEEDBACK.some((p) => pathname === p || pathname.startsWith(p + '/'))

  // Mobile nav drawer. Desktop (lg+) is UNCHANGED — the sidebar is a static
  // in-flow column and this state is never surfaced (the toggle + overlay are
  // lg:hidden, and the drawer wrapper is lg:static / lg:translate-x-0). Below lg
  // the sidebar becomes an off-canvas drawer opened by the top-bar hamburger.
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Feedback panel state lives here because the TRIGGER lives in two places:
  // the mobile header below lg, and PageFeedback's floating pill at lg+. The
  // panel itself is rendered by PageFeedback either way.
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  // Close the drawer whenever the route changes so a nav tap doesn't leave it open.
  useEffect(() => setDrawerOpen(false), [pathname])

  // Desktop focus mode (see NAV_COLLAPSED_KEY). Deliberately separate from
  // drawerOpen: that one is the <lg off-canvas drawer, this one only applies at
  // lg+ where the sidebar is an in-flow column. localStorage is read lazily in
  // the initialiser so the first paint is already in the right state (no flash).
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem(NAV_COLLAPSED_KEY) === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem(NAV_COLLAPSED_KEY, navCollapsed ? '1' : '0') } catch { /* private mode */ }
  }, [navCollapsed])

  return (
    // h-dvh, NOT h-screen. h-screen is height:100vh, and on iOS Safari 100vh is
    // the viewport WITHOUT browser chrome — i.e. taller than what you can see.
    // Paired with overflow-hidden here, that pushed the shell's bottom ~60-100px
    // under Safari's toolbar with no way to scroll to it: reported from a real
    // iPhone 14 Pro as the Pipeline board being unscrollable and /ai's pinned
    // context cut off at the bottom. dvh tracks the VISIBLE viewport, so the
    // shell always fits.
    //
    // Headless Chromium cannot reproduce this — its viewport is exact, so
    // 100vh == the visible height and everything measures as reachable. Verified:
    // the mobile sweep passed /pipeline throughout. That is why the guard for it
    // is a static test (tests/test_viewport_units.py), not a browser assertion.
    //
    // supports-[] keeps a 100vh fallback for anything predating dvh (Safari
    // <15.4 / Chrome <108), where the old behaviour is still better than an
    // unset height.
    <div className="flex h-screen supports-[height:100dvh]:h-dvh overflow-hidden bg-slate-50">
      {/* Keyboard users otherwise tab through ~30 sidebar links on every page
          before reaching content. Invisible until focused. */}
      <a
        href="#main-content"
        className="penser-skip-link"
        onClick={(e) => {
          // Focus <main> directly instead of letting the hash land in the URL.
          e.preventDefault()
          document.getElementById('main-content')?.focus()
        }}
      >
        Skip to main content
      </a>
      {/* Mobile-only backdrop behind the open drawer. */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Sidebar container:
          - mobile (<lg): fixed off-canvas drawer, slides in/out via translate
          - desktop (lg+): static, in-flow column — identical to before */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-60 transform transition-transform duration-200 ease-out',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
          // lg:relative (not lg:static) so the collapse handle below, which is
          // absolutely positioned, anchors to THIS column at desktop instead of
          // escaping to the viewport.
          'lg:relative lg:z-auto lg:translate-x-0 lg:transition-none',
          // Focus mode is desktop-only: below lg the drawer must still be able to
          // open, so this hides the column at lg+ ONLY.
          navCollapsed && 'lg:hidden',
        )}
      >
        <Sidebar />
        {/* Collapse handle, pinned to the column's inner edge. Desktop-only —
            on mobile the drawer closes by tapping the backdrop. */}
        <button
          type="button"
          onClick={() => setNavCollapsed(true)}
          aria-label="Hide navigation (focus mode)"
          title="Hide navigation — focus mode"
          className="absolute right-1 top-2 hidden rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 lg:block"
        >
          <PanelLeftClose size={15} />
        </button>
      </div>

      {/* Reopen affordance while collapsed. Sits on the far left edge so it's
          findable without being in the way of content. */}
      {navCollapsed && (
        <button
          type="button"
          onClick={() => setNavCollapsed(false)}
          aria-label="Show navigation"
          title="Show navigation"
          className="fixed left-0 top-3 z-40 hidden rounded-r-md border border-l-0 border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm hover:bg-slate-50 hover:text-penser-blue-dark lg:block"
        >
          <PanelLeftOpen size={16} />
        </button>
      )}

      {/* Content column: on mobile it carries a top bar with the menu button;
          on desktop the top bar is hidden (lg:hidden) and this is just <main>. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100"
          >
            <Menu size={20} />
          </button>
          <img src="/merged_logo_1.svg" alt="Penser · Innovative Claim Solutions · Arissa" className="h-6" />
          {/* Mobile mirror of the sidebar badge — the sidebar is off-canvas
              here, so this is the only always-visible env marker. */}
          <EnvBadge />
          {/* Mobile Feedback trigger. PageFeedback's floating pill is
              hidden lg:flex, so this is the only way to reach the panel below
              lg — deliberately, because a position:fixed z-40 pill painted over
              row actions in the bottom-right corner (measured: "Convert" on
              /prospects 1px from its edge). ml-auto pushes it to the far right,
              opposite the hamburger. */}
          {!hideFeedback && (
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              aria-label="Send feedback or report a bug"
              className="ml-auto rounded-md p-1.5 text-slate-600 hover:bg-slate-100"
            >
              <MessageSquareDot size={20} />
            </button>
          )}
        </header>

        {/* No bottom padding needed: below lg the Feedback trigger is in the
            header above, not a fixed overlay, so nothing covers page content.
            An earlier attempt reserved pb-16 here to clear the floating pill —
            that was the wrong fix. Padding only guarantees the LAST content can
            be scrolled clear; a position:fixed overlay covers whatever passes
            beneath it at every other scroll position. Moving the trigger
            removes the overlay instead of working around it. */}
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
          {/* RouteErrorBoundary catches any render-time exception in the
              child route so the whole site can't blank. The sidebar stays
              interactive; the user gets a "this page hit an error" card
              with a retry + back-to-dashboard. Boundary auto-resets on
              navigation so the error doesn't follow the user around. */}
          <RouteErrorBoundary>
            {/* Suspense boundary for lazy-loaded feature slices — each feature is
                its own webpack chunk (see @/features/registry). */}
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2 size={22} className="animate-spin text-slate-300" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>

      {/* PageFeedback is a fixed-position overlay anchored to the viewport's
          bottom-right. Rendering it outside <main> keeps it from being
          clipped by the scroll container and ensures it overlays content
          on every page (Dashboard, Pipeline, AI, etc.). */}
      {!hideFeedback && (
        <PageFeedback open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      )}
      {/* B27: Cmd+K / Ctrl+K palette. Mounted at shell scope so the
          keyboard shortcut works on every page. Self-renders only when
          opened. */}
      <CommandPalette />
    </div>
  )
}
